/**
 * Stripe price ids of the subscription plans, read from public.subscription_prices (VOY-601).
 *
 * Each environment has its own Stripe account, so the price ids are per-environment data, not
 * code: switching accounts only needs new rows. The table is public-read (VOY-554 migration).
 * - Selling (a new checkout): the active `standard` row of the plan's tier and interval.
 * - Recognizing an existing subscription: ANY row, active or not, since a retired price can
 *   still be carried by a subscriber.
 *
 * There is no Stripe mode signal in the frontend: each environment must keep active rows of ONE
 * stripe_mode only. If two active standard rows match the same tier and interval, no price is
 * sold (fail closed) and the conflict is logged. Never fall back to a hardcoded id. The lookups
 * over these rows are getPriceId / getPlanFromPriceId in stripe-config.
 *
 * @module subscription-prices
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface SubscriptionPrice {
  stripe_price_id: string;
  tier_id: string;
  interval: string | null;
  kind: string | null;
  active: boolean;
}

// Loaded once per page load and shared by every caller. A failed load is not cached, so the
// next caller retries.
let cached: SubscriptionPrice[] | null = null;
let inflight: Promise<SubscriptionPrice[]> | null = null;

/** Drop the shared cache (tests). */
export function clearCachedSubscriptionPrices(): void {
  cached = null;
  inflight = null;
}

/**
 * Load every subscription_prices row. Rejects on a failed read or an empty table (no plan could
 * be sold or recognized), so callers fail closed instead of treating it as "no prices".
 */
export function loadSubscriptionPrices(): Promise<SubscriptionPrice[]> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  const promise = (async () => {
    const { data, error } = await supabase
      .from('subscription_prices')
      .select('stripe_price_id, tier_id, interval, kind, active');
    if (error) throw new Error(`subscription_prices read failed: ${error.message}`);
    const rows = (data ?? []) as unknown as SubscriptionPrice[];
    if (rows.length === 0) throw new Error('subscription_prices is empty');
    cached = rows;
    return rows;
  })();
  inflight = promise;
  void promise.then(
    () => {
      if (inflight === promise) inflight = null;
    },
    (e) => {
      if (inflight === promise) inflight = null;
      console.error('[subscription-prices] Failed to load subscription prices:', e);
    }
  );
  return promise;
}

export type SubscriptionPricesStatus = 'loading' | 'ready' | 'error';

/** The subscription prices for components. `prices` is empty unless status is 'ready'. */
export function useSubscriptionPrices(): { status: SubscriptionPricesStatus; prices: SubscriptionPrice[] } {
  const [state, setState] = useState<{ status: SubscriptionPricesStatus; prices: SubscriptionPrice[] }>(() =>
    cached ? { status: 'ready', prices: cached } : { status: 'loading', prices: [] }
  );

  useEffect(() => {
    if (state.status === 'ready') return;
    let active = true;
    loadSubscriptionPrices().then(
      (prices) => {
        if (active) setState({ status: 'ready', prices });
      },
      () => {
        if (active) setState({ status: 'error', prices: [] });
      }
    );
    return () => {
      active = false;
    };
    // Load once per mount; a failed load is retried on the next mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
